// Task #289 — global client error reporter.
//
// Captures uncaught JS errors (window "error") and unhandled promise
// rejections ("unhandledrejection") and reports them to the existing
// unauthenticated /api/client-errors sink, which persists them into
// system_events (category "app") — visible in CRM > Salute Sistema.
//
// Complements the React ErrorBoundary (render errors): this catches errors
// OUTSIDE the React render path (event handlers, async code, third-party
// scripts). Best-effort: never throws, deduped and capped per session.

import {
  classifyResourceLoadError,
  sanitizeTelemetryText,
  sanitizeTelemetryUrl,
  type ResourceType,
} from "@shared/client-error-telemetry";
import {
  getClientRuntimeDiagnostics,
  inferClientErrorAttribution,
} from "./client-error-diagnostics";
import {
  BROWSER_RECOVERY_EVENT,
  wasBrowserRecoveryHandled,
} from "./browser-recovery";

const DEDUPE_WINDOW_MS = 30_000;
const MAX_REPORTS_PER_SESSION = 10;

let reportsSent = 0;
// One bounded map is shared by JS and resource telemetry.
const recentReports = new Map<string, number>();
const MAX_UNIQUE_RESOURCES_PER_SESSION = 500;
// Resource keys are intentionally session-scoped rather than windowed: a
// resource that fails again after the 30s message window is still one failure
// for the threshold metric.
const uniqueResourceKeys = new Set<string>();

type ReportResult = "new" | "sent" | "suppressed" | "deduped";
type ReportEligibility = "new" | "suppressed" | "deduped";

function shouldReport(key: string, bypassBudget = false): ReportEligibility {
  const now = Date.now();
  const last = recentReports.get(key);
  if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return "deduped";
  recentReports.set(key, now);
  // Bound the dedupe map.
  if (recentReports.size > 50) {
    const oldest = recentReports.keys().next().value;
    if (oldest !== undefined) recentReports.delete(oldest);
  }
  // Mark the key even when the session budget is exhausted. Resource
  // threshold accounting must remain observable and can emit its one
  // escalation with bypassBudget=true.
  if (!bypassBudget && reportsSent >= MAX_REPORTS_PER_SESSION) return "suppressed";
  return "new";
}

function report(
  source:
    | "client-error-boundary"
    | "window-onerror"
    | "unhandledrejection"
    | "hydration-error"
    | "resource-load-error",
  message: string,
  stack?: string,
  extra?: {
    dedupeKey?: string;
    bypassBudget?: boolean;
    componentStack?: string;
    route?: string;
    resourceUrl?: string;
    resourceType?: ResourceType;
    classification?: string;
    responseStatus?: number | null;
    online?: boolean | null;
    exceededThreshold?: boolean;
    resourceFailuresThisSession?: number;
    sampleUrls?: string[];
    isChunkError?: boolean;
    reloadSuppressed?: boolean;
    omitUrl?: boolean;
    recoveryKind?: string;
  },
): ReportResult {
  const msg = sanitizeTelemetryText(message || "unknown", 1000);
  const key = `${source}:${extra?.dedupeKey || msg}`;
  const eligibility = shouldReport(key, extra?.bypassBudget === true);
  if (eligibility !== "new") return eligibility;
  if (!extra?.bypassBudget) reportsSent++;
  try {
    const diagnostics = getClientRuntimeDiagnostics();
    const online =
      typeof navigator !== "undefined" && typeof navigator.onLine === "boolean"
        ? navigator.onLine
        : null;
    fetch("/api/client-errors", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        source,
        message: msg,
        stack: sanitizeTelemetryText(stack || "", 4000),
        url: extra?.omitUrl ? "" : sanitizeTelemetryUrl(window.location.href),
        ...(extra?.componentStack ? { componentStack: sanitizeTelemetryText(extra.componentStack, 4000) } : {}),
        ...(extra?.route ? { route: sanitizeTelemetryUrl(extra.route, 300) } : {}),
        ...(extra?.resourceUrl ? { resourceUrl: sanitizeTelemetryUrl(extra.resourceUrl) } : {}),
        ...(extra?.resourceType ? { resourceType: extra.resourceType } : {}),
        ...(extra?.classification ? { classification: extra.classification } : {}),
        ...(extra?.responseStatus !== undefined ? { responseStatus: extra.responseStatus } : {}),
        ...(extra?.online !== undefined ? { online: extra.online } : {}),
        ...(extra?.exceededThreshold ? { exceededThreshold: true } : {}),
        ...(extra?.resourceFailuresThisSession !== undefined
          ? { resourceFailuresThisSession: extra.resourceFailuresThisSession }
          : {}),
        ...(extra?.sampleUrls ? { sampleUrls: extra.sampleUrls.slice(0, 5).map((url) => sanitizeTelemetryUrl(url)) } : {}),
        ...(extra?.isChunkError !== undefined ? { isChunkError: extra.isChunkError } : {}),
        ...(extra?.reloadSuppressed !== undefined ? { reloadSuppressed: extra.reloadSuppressed } : {}),
        attribution: inferClientErrorAttribution(stack, extra?.componentStack),
        ...(diagnostics.clientBuildId ? { clientBuildId: diagnostics.clientBuildId } : {}),
        ...(diagnostics.browserFamily ? { browserFamily: diagnostics.browserFamily } : {}),
        ...(diagnostics.browserMajor !== undefined ? { browserMajor: diagnostics.browserMajor } : {}),
        supportsArrayAt: diagnostics.supportsArrayAt,
        supportsStringAt: diagnostics.supportsStringAt,
        ...(extra?.recoveryKind ? { recoveryKind: extra.recoveryKind } : {}),
        ...(online !== null && extra?.online === undefined ? { online } : {}),
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // never break the app because of error reporting
  }
  return "sent";
}

// ---------------------------------------------------------------------------
// Task #382 — React hydration / recoverable render errors.
// The app uses createRoot (not hydrateRoot) by design (see catalog SSR parity
// notes): React replaces the SSR shell instead of hydrating it. Recoverable
// errors reported by React through onRecoverableError still indicate a
// client/server markup or render mismatch worth observing. They are logged
// server-side as WARN (category "app", source "hydration-error") and are
// visible in CRM > Salute Sistema.
// ---------------------------------------------------------------------------
export function reportRecoverableReactError(error: unknown, errorInfo?: { componentStack?: string }): void {
  const err = error as { message?: string; stack?: string } | undefined;
  const message =
    (err && typeof err.message === "string" && err.message) ||
    (typeof error === "string" ? error : "recoverable React error");
  report("hydration-error", message, err?.stack, {
    componentStack: (errorInfo?.componentStack || "").slice(0, 4000),
    route: window.location.pathname,
  });
}

// ---------------------------------------------------------------------------
// Task #488 — resource-load monitoring. Resource-load errors don't bubble, so
// this uses a capture-phase listener. A browser error event does not prove a
// 404: only PerformanceResourceTiming.responseStatus can provide HTTP evidence.
// The same listener covers images, scripts, stylesheets and other elements.
// ---------------------------------------------------------------------------
const IMG_404_ALERT_THRESHOLD = 5;
let resourceFailuresThisSession = 0;
const resourceSampleUrls: string[] = [];
let img404ThresholdReported = false;
let resourceMonitoringInitialized = false;

function resourceTypeForTarget(target: EventTarget | null): ResourceType {
  if (typeof HTMLImageElement !== "undefined" && target instanceof HTMLImageElement) return "image";
  if (typeof HTMLScriptElement !== "undefined" && target instanceof HTMLScriptElement) return "script";
  if (
    typeof HTMLLinkElement !== "undefined" &&
    target instanceof HTMLLinkElement &&
    /\bstylesheet\b/i.test(target.rel || "")
  ) {
    return "stylesheet";
  }
  return "element";
}

function resourceUrlForTarget(target: EventTarget | null): string {
  if (typeof HTMLImageElement !== "undefined" && target instanceof HTMLImageElement) {
    return target.currentSrc || target.src || "";
  }
  if (typeof HTMLScriptElement !== "undefined" && target instanceof HTMLScriptElement) return target.src || "";
  if (typeof HTMLLinkElement !== "undefined" && target instanceof HTMLLinkElement) return target.href || "";
  const element = target as { currentSrc?: unknown; src?: unknown; href?: unknown } | null;
  return typeof element?.currentSrc === "string"
    ? element.currentSrc
    : typeof element?.src === "string"
      ? element.src
      : typeof element?.href === "string"
        ? element.href
        : "";
}

function responseStatusForResource(url: string): number | null {
  if (!url || typeof performance === "undefined" || typeof performance.getEntriesByName !== "function") {
    return null;
  }
  try {
    const entries = performance.getEntriesByName(url);
    for (let i = entries.length - 1; i >= 0; i -= 1) {
      const status = (entries[i] as PerformanceResourceTiming & { responseStatus?: unknown }).responseStatus;
      if (typeof status === "number" && Number.isInteger(status) && status >= 100 && status <= 599) {
        return status;
      }
    }
  } catch {
    // Resource Timing can be unavailable or restricted for cross-origin URLs.
  }
  return null;
}

export function initBrokenImageMonitoring(): void {
  if (resourceMonitoringInitialized) return;
  resourceMonitoringInitialized = true;
  window.addEventListener(
    "error",
    (event) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      // The index.html early asset hook handles critical module/CSS failures
      // before this bundle exists. It marks the same event so this listener
      // does not create a duplicate resource metric or recovery report.
      if ((event as { __uoRecoveryHandled?: unknown }).__uoRecoveryHandled === true) return;
      // The preload/recovery worker may already have claimed this element's
      // failure. Its WeakSet marker is shared to avoid a second telemetry
      // event when the native element error arrives afterwards.
      if (wasBrowserRecoveryHandled(event) || wasBrowserRecoveryHandled(target)) return;
      const src = resourceUrlForTarget(target);
      if (!src || src.startsWith("data:") || src.startsWith("blob:")) return;
      const resourceType = resourceTypeForTarget(target);
      const responseStatus = responseStatusForResource(src);
      const online = typeof navigator !== "undefined" && navigator.onLine === false ? false : null;
      const classification = classifyResourceLoadError({ online, responseStatus });
      const sanitizedSrc = sanitizeTelemetryUrl(src);
      const resourceKey = `${resourceType}:${sanitizedSrc}`;
      if (uniqueResourceKeys.has(resourceKey)) return;
      uniqueResourceKeys.add(resourceKey);
      if (uniqueResourceKeys.size > MAX_UNIQUE_RESOURCES_PER_SESSION) {
        const oldest = uniqueResourceKeys.values().next().value;
        if (oldest !== undefined) uniqueResourceKeys.delete(oldest);
      }
      const reportResult = report(
        "resource-load-error",
        `${resourceType} failed to load (${classification})`,
        undefined,
        {
          dedupeKey: resourceKey,
          resourceUrl: src,
          resourceType,
          classification,
          responseStatus,
          online,
          route: window.location.pathname,
        },
      );
      // Count a newly observed resource even if the normal per-session report
      // budget suppressed its individual event; the threshold escalation has
      // its own dedupe key and bypasses that budget.
      if (reportResult === "deduped") return;
      resourceFailuresThisSession += 1;
      if (resourceSampleUrls.length < 5) resourceSampleUrls.push(sanitizedSrc);
      if (resourceFailuresThisSession > IMG_404_ALERT_THRESHOLD && !img404ThresholdReported) {
        img404ThresholdReported = true;
        report(
          "resource-load-error",
          `More than ${IMG_404_ALERT_THRESHOLD} failed resources in one session (${resourceFailuresThisSession})`,
          undefined,
          {
            dedupeKey: "resource-threshold",
            bypassBudget: true,
            exceededThreshold: true,
            route: window.location.pathname,
            resourceFailuresThisSession,
            sampleUrls: resourceSampleUrls,
          },
        );
      }
    },
    true, // capture phase: resource errors do not bubble
  );
}

let initialized = false;

export function initGlobalErrorReporting(): void {
  if (initialized) return;
  initialized = true;

  window.addEventListener(BROWSER_RECOVERY_EVENT, (event) => {
    const detail = (event as CustomEvent<{
      telemetry?: boolean;
      kind?: string;
      offline?: boolean;
      reloadSuppressed?: boolean;
    }>).detail;
    if (!detail?.telemetry || detail.reloadSuppressed !== true) return;
    report(
      "client-error-boundary",
      "Browser recovery suppressed",
      undefined,
      {
        dedupeKey: "browser-recovery-suppressed",
        isChunkError: true,
        reloadSuppressed: true,
        omitUrl: true,
        classification: detail.offline ? "offline" : detail.kind || "css-preload",
        recoveryKind: detail.offline ? "offline" : detail.kind || "css-preload",
        online: detail.offline ? false : undefined,
      },
    );
  });

  window.addEventListener("error", (event) => {
    // Resource-load errors (img/script tags) fire "error" without an Error
    // object and with no message; skip those to avoid noise.
    if (!event.message && !event.error) return;
    const err = event.error as Error | undefined;
    if (wasBrowserRecoveryHandled(err ?? event.error)) return;
    report(
      "window-onerror",
      err?.message || event.message || "unknown error",
      err?.stack,
    );
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason: unknown = event.reason;
    if (wasBrowserRecoveryHandled(reason)) return;
    if (reason instanceof Error) {
      report("unhandledrejection", reason.message, reason.stack);
    } else {
      let msg: string;
      try {
        msg = typeof reason === "string" ? reason : JSON.stringify(reason);
      } catch {
        msg = String(reason);
      }
      report("unhandledrejection", msg || "unhandled rejection");
    }
  });
}
